import React, { useId } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { PRODUCT_TYPES } from '../../utils/constants';
import { createFormatter } from '../../helpers/formatters';
import getProductType from '../../helpers/getProductType';

export const AddProductModal = ({
  getProductVisualTotal,
  isProcessing,
  onAddProduct,
  onHide,
  onProductSelect,
  onQuantityChange,
  onSearchChange,
  onSelectedWeightChange,
  searchedProducts,
  selectedProduct,
  show,
}) => {
  const id = useId();

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Añadir producto a la orden</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label htmlFor="search-term">Buscar producto</Form.Label>
          <Form.Control
            type="text"
            id="search-term"
            aria-describedby={id + '-search'}
            placeholder="alitas kosher, papas a la francesa..."
            onChange={onSearchChange}
          />
          <Form.Text id={id + '-search'} muted>
            Término de búsqueda textual. Busca en varios campos del producto,
            como Nombre, SKU y Descripción, etc.
          </Form.Text>
        </Form.Group>

        <Form.Group>
          <Form.Label htmlFor="selected-product"></Form.Label>
          <Form.Select
            id="selected-product"
            aria-describedby={id + '-selected'}
            onChange={onProductSelect}
          >
            <option>Selecciona un producto</option>
            {!!searchedProducts.length &&
              searchedProducts.map(({ node }) => (
                <option key={node.entityId} value={node.entityId}>
                  {getProductType(node.customFields.edges)} | {node.name} |{' '}
                  {node.sku}
                </option>
              ))}
          </Form.Select>

          {!!selectedProduct && (
            <>
              <Form.Text id={id + '-selected'} muted>
                Producto: <br /> Tipo:{' '}
                {getProductType(selectedProduct.customFields.edges)} |{' '}
                {selectedProduct.name} | SKU: {selectedProduct.sku} | Price:{' '}
                {createFormatter(
                  'es-MX',
                  selectedProduct.prices?.price?.currencyCode
                ).format(
                  selectedProduct.prices.salePrice?.value ||
                    selectedProduct.prices.price?.value
                )}
              </Form.Text>

              <Form.Group>
                <Form.Label htmlFor={id + '-quantity'}>Cantidad</Form.Label>
                <Form.Control
                  required
                  id={id + '-quantity'}
                  type={
                    PRODUCT_TYPES.KILOGRAM ===
                    getProductType(selectedProduct.customFields.edges)
                      ? 'text'
                      : 'number'
                  }
                  max={999}
                  min={1}
                  maxLength={6}
                  onChange={onQuantityChange}
                />

                {PRODUCT_TYPES.PIECE_KILO ===
                  getProductType(selectedProduct.customFields.edges) && (
                  <>
                    <Form.Label htmlFor={id + '-pzkg'}>
                      Peso seleccionado total
                    </Form.Label>
                    <Form.Control
                      id={id + '-pzkg'}
                      type="text"
                      min={1}
                      maxLength={6}
                      onChange={onSelectedWeightChange}
                    />
                  </>
                )}
              </Form.Group>
              <Form.Text muted>
                El precio final se calcula con el precio vigente del producto.
                Después de agregarlo podrás editar su cantidad o quitarlo desde
                la tabla (excepto ajustes de pago y envío).
              </Form.Text>
            </>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        {!!selectedProduct && (
          <span style={{ width: '50%' }}>
            <b>Total del producto:</b>{' '}
            {createFormatter().format(getProductVisualTotal())}
          </span>
        )}
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
        <Button
          variant="primary"
          onClick={onAddProduct}
          disabled={isProcessing || !selectedProduct}
          style={{
            backgroundColor: isProcessing ? '#6c757d' : '#007bff',
          }}
        >
          {isProcessing ? 'Añadiendo...' : 'Añadir'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const ConfirmCaptureFundsModal = ({
  isProcessing,
  onConfirm,
  onHide,
  show,
  amountToCharge = null,
  usedRecalc = false,
}) => {
  const formattedAmount =
    amountToCharge !== null && !isNaN(Number(amountToCharge))
      ? createFormatter().format(Number(amountToCharge))
      : null;

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Confirmación</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        ¿Seguro que quiere realizar cobro?
        {formattedAmount && (
          <div style={{ marginTop: 12 }}>
            <strong>Monto a cobrar:</strong> {formattedAmount}
            {usedRecalc && (
              <div style={{ fontSize: 12, color: '#8a6d3b', marginTop: 4 }}>
                Total recalculado tras aplicar cupones.
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          disabled={isProcessing}
          style={{
            backgroundColor: isProcessing ? '#6c757d' : '#007bff',
          }}
        >
          {isProcessing ? 'Capturando...' : 'Confirmar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const ConfirmPickupModal = ({
  isProcessing,
  onConfirm,
  onHide,
  show,
}) => (
  <Modal show={show} onHide={onHide}>
    <Modal.Header closeButton>
      <Modal.Title>Confirmación</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      ¿Seguro que quiere cambiar el estatus a "Listo y en espera de
      recolección"?
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Cerrar
      </Button>
      <Button
        variant="primary"
        onClick={onConfirm}
        disabled={isProcessing}
        style={{
          backgroundColor: isProcessing ? '#6c757d' : '#007bff',
        }}
      >
        {isProcessing ? 'Procesando...' : 'Confirmar'}
      </Button>
    </Modal.Footer>
  </Modal>
);

export const ConfirmSendAndPayLaterModal = ({
  isProcessing,
  onConfirm,
  onHide,
  show,
}) => (
  <Modal show={show} onHide={onHide}>
    <Modal.Header closeButton>
      <Modal.Title>Confirmación</Modal.Title>
    </Modal.Header>
    <Modal.Body>¿Seguro que quiere cambiar el estatus a "En Ruta"?</Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Cerrar
      </Button>
      <Button
        variant="primary"
        onClick={onConfirm}
        disabled={isProcessing}
        style={{
          backgroundColor: isProcessing ? '#6c757d' : '#007bff',
        }}
      >
        {isProcessing ? 'Procesando...' : 'Confirmar'}
      </Button>
    </Modal.Footer>
  </Modal>
);
